// src/components/rep/RepDashboard.js
import { useMemo } from 'react';
import { Users, Package, DollarSign, TrendingUp, Clock } from 'lucide-react';

export function RepDashboard({ customers, orders, userData }) {
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    // Filter orders for this rep's customers
    const customerIds = customers.map(c => c.id);
    const repOrders = orders.filter(o => customerIds.includes(o.customerId));

    const thisMonthOrders = repOrders.filter(o => {
      const date = o.createdAt?.toDate?.() || new Date(o.createdAt);
      return date >= thisMonth;
    });

    const pendingOrders = repOrders.filter(o => o.status === 'New' || o.status === 'Processing');

    // Revenue (for commission calculation)
    const totalRevenue = repOrders.reduce((sum, o) => sum + (o.subtotal || 0), 0);
    const thisMonthRevenue = thisMonthOrders.reduce((sum, o) => sum + (o.subtotal || 0), 0);

    // Commission
    const commissionRate = userData?.commissionRate || 0;
    const commissionType = userData?.commissionType || 'percentage';
    
    let totalCommission = 0;
    if (commissionType === 'percentage') {
      totalCommission = totalRevenue * (commissionRate / 100);
    } else {
      const totalUnits = repOrders.reduce((sum, o) => 
        sum + o.items?.reduce((s, i) => s + i.quantity, 0) || 0, 0);
      totalCommission = totalUnits * commissionRate;
    }

    const commissionOwed = totalCommission - (userData?.totalCommissionPaid || 0);

    return {
      totalCustomers: customers.length,
      activeCustomers: customers.filter(c => c.totalOrders > 0).length,
      totalOrders: repOrders.length,
      thisMonthOrders: thisMonthOrders.length,
      pendingOrders: pendingOrders.length,
      totalRevenue,
      thisMonthRevenue,
      totalCommission,
      commissionOwed
    };
  }, [customers, orders, userData]);

  // Recent orders
  const recentOrders = useMemo(() => {
    const customerIds = customers.map(c => c.id);
    return orders
      .filter(o => customerIds.includes(o.customerId))
      .slice(0, 5);
  }, [customers, orders]);

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Dashboard</h2>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">My Customers</p>
              <p className="text-2xl font-bold">{stats.totalCustomers}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Users className="text-blue-600" size={24} />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">{stats.activeCustomers} with orders</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Orders</p>
              <p className="text-2xl font-bold">{stats.totalOrders}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Package className="text-purple-600" size={24} />
            </div>
          </div>
          {stats.pendingOrders > 0 && (
            <p className="mt-2 text-sm text-yellow-600">{stats.pendingOrders} pending</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">This Month Sales</p>
              <p className="text-2xl font-bold">${stats.thisMonthRevenue.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <TrendingUp className="text-green-600" size={24} />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">{stats.thisMonthOrders} orders</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Commission Owed</p>
              <p className="text-2xl font-bold text-green-600">${stats.commissionOwed.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <DollarSign className="text-yellow-600" size={24} />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            {userData?.commissionType === 'percentage' 
              ? `${userData?.commissionRate}% rate`
              : `$${userData?.commissionRate}/unit`}
          </p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4">Recent Orders</h3>
          {recentOrders.length === 0 ? (
            <p className="text-gray-500 text-sm">No orders yet</p>
          ) : (
            <div className="space-y-3">
              {recentOrders.map(order => {
                const customer = customers.find(c => c.id === order.customerId);
                const date = order.createdAt?.toDate?.() || new Date(order.createdAt);
                return (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{customer?.companyName || 'Unknown'}</p>
                      <p className="text-xs text-gray-500">#{order.id.slice(-8).toUpperCase()}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${(order.subtotal || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{date.toLocaleDateString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Customers */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4">Top Customers</h3>
          {customers.length === 0 ? (
            <p className="text-gray-500 text-sm">No customers yet</p>
          ) : (
            <div className="space-y-3">
              {[...customers]
                .sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0))
                .slice(0, 5)
                .map(customer => (
                  <div key={customer.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{customer.companyName}</p>
                      <p className="text-xs text-gray-500">{customer.totalOrders || 0} orders</p>
                    </div>
                    <p className="font-medium">${(customer.totalSpent || 0).toFixed(2)}</p>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
